import { Component, OnDestroy, OnInit, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { VideoBackgroundComponent } from 'src/app/shared/ui-common/video-background/video-background.component';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { OnDemandService } from './data/on-demand.service';
import { OnDemandResponse, OnDemandType } from './data/on-demand.model';
import { Subscription } from 'rxjs';
import { OnDemandScenarioEnum } from './data/on-demand-scenario.enum';
import { OpenCaseComponent } from './open-case/open-case.component';
import { SharedService } from 'src/app/shared/data/shared.service';
import { Account } from 'src/app/shared/data/models/account.model';
import { MarkdownPipe } from "../../shared/data/pipes/markdown.pipe";
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { environment } from 'src/environments/environment.uat';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { FeaturePillMenuComponent } from 'src/app/shared/feature-pill-menu/feature-pill-menu.component';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';

interface SuccessStoryQuote {
  text: string;
  author: string;
}

@Component({
  selector: 'app-feature-on-demand',
  imports: [
    CommonModule,
    VideoBackgroundComponent,
    ButtonComponent,
    OpenCaseComponent,
    MarkdownPipe,
    TranslatePipe,
    PillComponent,
    FeaturePillMenuComponent
],
  templateUrl: './feature-on-demand.component.html',
  styleUrl: './feature-on-demand.component.scss',
})
export class FeatureOnDemandComponent implements OnInit, OnDestroy {
  @ViewChild('quoteText') quoteTextElement!: ElementRef<HTMLDivElement>;
  @ViewChild('quoteAuthor') quoteAuthorElement!: ElementRef<HTMLDivElement>;
  @ViewChild('howItWorks') howItWorksElement!: ElementRef<HTMLDivElement>;
  @ViewChild('pillMenu') pillMenuElement!: FeaturePillMenuComponent;

  isMobile = false;
  s3BaseUrl = environment.s3BaseUrl;
  type: OnDemandType = 'mind';
  callConfirmedSignal = signal<boolean>(false);
  onDemandResponseSignal = signal<OnDemandResponse | null>(null);
  onDemandScenarios = OnDemandScenarioEnum;
  account: Account | null = null;
  showResponseTeamVideo = false;
  languageId = '1045';
  fadeOut = false;
  fadeIn = false;
  currentQuoteIndex = 0;
  videoFormat = '.mp4';
  // TODO: replace with real quotes
  mindQuotes = [
    {
      text: "I had a very good conversation where I received some very good advise and direction on how to navigate my current situation",
      author: "Anonymous Howdy User"
    },
    {
      text: "The support I received was exactly what I needed during a difficult time",
      author: "Anonymous Howdy User"
    },
    {
      text: "Professional, caring, and incredibly helpful guidance",
      author: "Anonymous Howdy User"
    }
  ];
  bodyQuotes = [
    {
      text: "I had a very good conversation where I received some very good advise and direction on how to navigate my current situation",
      author: "Anonymous Howdy User"
    }
  ];
  pillExpanded = false;

  private routeSubscription?: Subscription;

  get successStoriesBackground(): string {
    const mindGradient = `linear-gradient(0deg, rgba(30, 84, 134, 0.20) 0%,rgba(30, 84, 134, 0.20) 100%),
      linear-gradient(0deg, rgba(0, 0, 0, 0.69) 0%, rgba(0, 0, 0, 0.69) 100%)`;
    const bodyGradient = `linear-gradient(0deg, rgba(41, 13, 13, 0.34) 0%,rgba(41, 13, 13, 0.34) 100%),
      linear-gradient(0deg, rgba(0, 0, 0, 0.69) 0%, rgba(0, 0, 0, 0.69) 100%)`;

    return this.type === 'mind' ? mindGradient : bodyGradient;
  }

  get background(): string {
    const mindGradient = 'linear-gradient(0deg, rgba(1, 21, 30, 0.90) 34.64%, rgba(1, 21, 30, 0.00) 76.38%)';
    const bodyGradient = `linear-gradient(0deg, rgba(52, 0, 0, 0.28) 0%,
      rgba(52, 0, 0, 0.28) 100%),
      linear-gradient(0deg, rgba(1, 21, 30, 0.90) 34.64%,
      rgba(1, 21, 30, 0.00) 76.38%)`;

    return this.type === 'mind' ? mindGradient : bodyGradient;
  }

  get progressBarIcon(): string {
    return this.type === 'mind' ? 'fa-solid fa-brain' : 'fa-solid fa-person';
  }

  get labelsKey(): string {
    return this.type === 'mind' ? 'OnDemandPsychologist' : 'OnDemandBody';
  }

  get videoName(): string {
    return this.type === 'mind' ? 'sunset-lake' : 'man_on_hill';
  }

  get descriptionLabel(): string {
    const labelKey = this.type === 'mind'
      ? 'App.v2.Views.OnDemandPsychologist.Who5required'
      : 'App.v2.Views.OnDemandBody.TakeBodySurvey';

    return this.onDemandResponseSignal()?.labels?.[labelKey] || '';
  }

  get successStoriesQuotes(): SuccessStoryQuote[] {
    return this.type === 'mind' ? this.mindQuotes : this.bodyQuotes;
  }

  get showHowItWorks(): boolean {
    return this.onDemandResponseSignal()?.scenario !== OnDemandScenarioEnum.OnGoingCase && !this.callConfirmedSignal();
  }

  get stepType(): string {
    return this.type.charAt(0).toUpperCase() + this.type.slice(1);
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private onDemandService: OnDemandService,
    private sharedService: SharedService,
    private translationService: TranslationService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 500;
    this.videoFormat = this.sharedService.getVideoFormat();
    this.account = this.sharedService.getAccountData();
    this.languageId = this.translationService.getLanguageId();
    this.routeSubscription = this.route.queryParams.subscribe(async params => {
      const newType = params['type'] as OnDemandType || 'mind';
      this.type = newType;
      await this.fetchOnDemandData();
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  private async fetchOnDemandData(): Promise<void> {
    try {
      const response = await this.onDemandService.fetchData(this.type);
      this.onDemandResponseSignal.set(response);
    } catch (error) {
      this.onDemandResponseSignal.set(null);
    }
  }

  goBack(): void {
    this.location.back();
  }

  onNavigateToSurvey(): void {
    const type = this.type === 'mind' ? 'who5' : 'body';
    this.router.navigate(['/survey'], {
      queryParams: {
        onDemand: true,
        type
      }
    });
  }

  onCancelCallRequest(): void {
    this.router.navigate(['/available-surveys']);
  }

  onCallRequest(): void {
    this.callConfirmedSignal.set(true);
    this.onDemandService.bookOnDemand(this.type).subscribe(response => {
      this.onDemandResponseSignal.set(response);
    });
  }

  onNoteTextChanged(note: string): void {
    this.onDemandService.updateNote(this.type, note).subscribe(response => {
      this.onDemandResponseSignal.set(response);
    });
  }

  scrollToHowItWorks(): void {
    if (this.howItWorksElement?.nativeElement) {
      this.howItWorksElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  openHowdyVideo(): void {
    this.showResponseTeamVideo = true;
    this.fadeIn = false;

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.fadeIn = true;
      }, 10);
    });
  }

  closeHowdyVideo(): void {
    this.fadeOut = true;
    this.fadeIn = false;

    setTimeout(() => {
      this.showResponseTeamVideo = false;
      this.fadeOut = false;
    }, 400);
  }

  goToQuote(index: number): void {
    if (index !== this.currentQuoteIndex) {
      if (this.quoteTextElement?.nativeElement && this.quoteAuthorElement?.nativeElement) {
        this.quoteTextElement.nativeElement.classList.add('fade-out');
        this.quoteAuthorElement.nativeElement.classList.add('fade-out');

        setTimeout(() => {
          this.currentQuoteIndex = index;

          this.quoteTextElement.nativeElement.classList.remove('fade-out');
          this.quoteAuthorElement.nativeElement.classList.remove('fade-out');

          this.quoteTextElement.nativeElement.classList.add('fade-in');
          this.quoteAuthorElement.nativeElement.classList.add('fade-in');
          setTimeout(() => {
            this.quoteTextElement.nativeElement.classList.remove('fade-in');
            this.quoteAuthorElement.nativeElement.classList.remove('fade-in');
          }, 300);
        }, 200);
      } else {
        this.currentQuoteIndex = index;
      }
    }
  }
}
